import React, { useState, useRef, useEffect } from 'react'
import { Button } from './button'
import { useNPS } from '../../hooks/useNPS'
import { useLanguage } from '../../context/LanguageContext'
import { Dialog, DialogContent } from './modal'

const scale = [
  { value: 0, label: '0', color: 'bg-red-800 text-white border-red-800', selected: 'bg-red-500 text-white border-red-500' },
  { value: 1, label: '1', color: 'bg-[#E95223] text-white', selected: 'bg-red-500 text-white border-red-500' },
  { value: 2, label: '2', color: 'bg-[#EA6F22] text-white', selected: 'bg-orange-500 text-white border-orange-400' },
  { value: 3, label: '3', color: 'bg-[#FDC729] text-white', selected: 'bg-yellow-500 text-gray-700 border-yellow-500' },
  { value: 4, label: '4', color: 'bg-[#C2D234] text-white', selected: 'bg-lime-500 text-white border-lime-500' },
  { value: 5, label: '5', color: 'bg-[#66B44E] text-white', selected: 'bg-green-500 text-white border-green-500' },
]

const defaultFeedbackReasons = [
  'Ease of Use',
  'App Performance',
  'Feature Satisfaction',
  'Reward Experience',
  'Security',
  'Design & Layout',
  'Overall Experience',
  'Customer Support',
]

/**
 * FeedbackModal - self-contained NPS modal
 * - NPS form dialog: bottom-right on desktop (md+), centered on mobile
 * - Thank you dialog: centered on all screen sizes
 *
 * @param {Object} props
 * @param {Boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {String} props.triggerType - NPS trigger type (first_login, offer_engagement, etc.)
 * @param {Object} props.npsData - Optional context data
 * @param {String} props.channel - Channel: IN_APP (default) or EMAIL
 */
const FeedbackModal = ({ open = false, onClose, triggerType = 'first_login', npsData = null, channel = 'IN_APP' }) => {
  const [selected, setSelected] = useState(null)
  const [selectedReason, setSelectedReason] = useState(null)
  const [comment, setComment] = useState('')
  const [showThankYou, setShowThankYou] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [autoSubmitting, setAutoSubmitting] = useState(false)
  const autoSubmitTimerRef = useRef(null)
  const { languageContent } = useLanguage()
  const { submitResponse, skip, releaseNPSLock, loading } = useNPS()

  const npsCopy = languageContent?.nps || {}

  const npsText = {
    title: npsCopy.title || 'We\'d love your feedback',
    subtitle: npsCopy.subtitle || 'How satisfied are you with this experience?',
    notSatisfied: npsCopy?.scale?.notSatisfied || 'Not satisfied',
    extremelySatisfied: npsCopy?.scale?.extremelySatisfied || 'Extremely satisfied',
    improvementQuestion: npsCopy.improvementQuestion || 'What area do you think needs improvement?',
    commentPlaceholder: npsCopy.commentPlaceholder || 'What area do you think needs improvement?',
    skipButton: npsCopy.skipButton || 'Do it Later',
    submitButton: npsCopy.submitButton || 'Submit Feedback',
    thankYouTitle: npsCopy.thankYouTitle || 'Thank you for your feedback!',
    thankYouMessage: npsCopy.thankYouMessage || 'Your feedback helps us improve our offers.',
    closeButton: npsCopy.closeButton || 'Close',
  }

  // Clean up auto-submit timer on unmount
  useEffect(() => {
    return () => {
      if (autoSubmitTimerRef.current) {
        clearTimeout(autoSubmitTimerRef.current)
      }
    }
  }, [])

  const localeReasons = Array.isArray(npsCopy.reasonsList)
    ? npsCopy.reasonsList
    : Array.isArray(npsCopy.reasons)
      ? npsCopy.reasons
      : Object.values(npsCopy.reasons || {})

  const feedbackReasons = localeReasons.length ? localeReasons : defaultFeedbackReasons

  const submitAndThank = async (ratingValue, commentValue = null, reasonValue = null) => {
    setSubmitError(false)
    const success = await submitResponse({
      triggerType,
      rating: ratingValue,
      channel,
      comment: commentValue,
      selectedReason: reasonValue,
      npsData,
    })

    if (success) {
      setShowThankYou(true)
    } else {
      setSubmitError(true)
    }
    setAutoSubmitting(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (selected === null) return
    await submitAndThank(selected, comment || null, selectedReason)
  }

  const handleRetry = async () => {
    if (selected === null) return
    await submitAndThank(selected, comment || null, selectedReason)
  }

  const handleRatingSelect = (rating) => {
    setSelected(rating)
    setSubmitError(false)
    // Clear any pending auto-submit timer
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current)
      autoSubmitTimerRef.current = null
    }
    // Auto-submit for satisfied users (ratings 3-5 in IN_APP channel only) with 2-second delay
    if (channel === 'IN_APP' && rating >= 3) {
      setAutoSubmitting(true)
      autoSubmitTimerRef.current = setTimeout(async () => {
        autoSubmitTimerRef.current = null
        await submitAndThank(rating, null, null)
      }, 2000)
    }
  }

  const handleSkip = async () => {
    // Clear any pending auto-submit timer
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current)
      autoSubmitTimerRef.current = null
      setAutoSubmitting(false)
    }
    await skip(triggerType)
    handleClose()
  }

  const handleClose = () => {
    // Clear any pending auto-submit timer
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current)
      autoSubmitTimerRef.current = null
      setAutoSubmitting(false)
    }
    releaseNPSLock()
    setShowThankYou(false)
    setSelected(null)
    setSelectedReason(null)
    setComment('')
    setSubmitError(false)
    if (onClose) onClose()
  }

  return (
    <>
      {/* NPS Form Dialog — bottom-right on desktop (md+), centered on mobile */}
      <Dialog
        open={open && !showThankYou}
        onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}
      >
        <DialogContent
          showHeaderImage={false}
          className="md:left-auto md:top-auto md:right-6 md:bottom-8 md:translate-x-0 md:translate-y-0 md:max-w-sm"
        >
          <form onSubmit={handleSubmit}>
            <div className="text-center">
              <p className="text-base font-bold text-black">{npsText.title}</p>
              <p className="text-sm font-medium mb-4 text-[#1E1E1E]">{npsText.subtitle}</p>
              <div className="flex justify-between gap-1 mt-6">
                {scale.map((item) => (
                  <button
                    type="button"
                    key={item.value}
                    className={`w-[42px] h-[42px] rounded-full flex items-center justify-center text-sm font-semibold border transition-colors duration-150
                      ${selected === item.value ? item.selected : item.color}
                      ${selected !== null && selected !== item.value ? 'opacity-40' : ''}
                    `}
                    onClick={() => handleRatingSelect(item.value)}
                    disabled={loading}
                    tabIndex={0}
                    aria-pressed={selected === item.value}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mt-1 mb-4">
                <span className="text-[9px] text-theme-sub-title">{npsText.notSatisfied}</span>
                <span className="text-[9px] text-theme-sub-title">{npsText.extremelySatisfied}</span>
              </div>
            </div>

            {/* Feedback Section: Only show after a rating is selected (0, 1, or 2), with CSS animation */}
            <div
              className={`feedback-section transition-all duration-300 ease-in-out overflow-hidden ${
                selected === null
                  ? 'hidden'
                  : selected >= 0 && selected <= 2
                    ? 'block max-h-[360px] opacity-100 block'
                    : 'max-h-0 opacity-0 mt-0 hidden'
              }`}
            >
              <p className="text-sm font-medium mb-2 text-left leading-tight">{npsText.improvementQuestion}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {feedbackReasons.map((label) => {
                  const isSelected = selectedReason === label
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`px-4 py-2 rounded-md border text-[9px] font-medium transition-colors
                        ${isSelected ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-500 border-gray-300'}
                      `}
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedReason(label)}
                      disabled={loading}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
              <textarea
                className="w-full px-4 min-h-[80px] border border-gray-300 rounded-md p-3 text-sm text-gray-600 placeholder-gray-400"
                placeholder={npsText.commentPlaceholder}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Error state - show when submission fails */}
            {submitError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 mb-3">Failed to submit your feedback.</p>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    type="button"
                    size="sm"
                    onClick={handleRetry}
                    className="text-[11px]"
                    disabled={loading}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="secondary"
                    type="button"
                    size="sm"
                    onClick={handleClose}
                    className="text-[11px]"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}

            {/* Auto-submit indicator for ratings 3-5 */}
            {autoSubmitting && !submitError && (
              <p className="text-xs text-gray-400 text-center mt-4">Submitting...</p>
            )}

            {/* Show buttons only for ratings 0-2 (ratings 3-5 auto-submit) */}
            {selected !== null && selected >= 0 && selected <= 2 && !submitError && (
              <div className="flex flex-col gap-4 mt-6 justify-center">
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  className="w-full text-[11px]"
                  disabled={loading}
                >
                  {npsText.submitButton}
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  size="lg"
                  onClick={handleSkip}
                  className="w-full text-[11px]"
                  disabled={loading}
                >
                  {npsText.skipButton}
                </Button>
              </div>
            )}

            {/* For no selection, show skip only */}
            {selected === null && !submitError && (
              <div className="flex flex-row gap-4 mt-6 justify-center">
                <Button
                  variant="secondary"
                  type="button"
                  size="lg"
                  onClick={handleSkip}
                  className="w-[120px] text-[11px]"
                  disabled={loading}
                >
                  {npsText.skipButton}
                </Button>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Thank You Dialog — always centered */}
      <Dialog
        open={showThankYou}
        onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}
      >
        <DialogContent showHeaderImage={false}>
          <div id="thank-you-modal" className="bg-white w-full max-w-md">
            <p className="text-xl font-semibold text-theme-primary">{npsText.thankYouTitle}</p>
            <span className="text-sm font-normal text-theme-secondary leading-none mt-5">{npsText.thankYouMessage}</span>
            <div className="flex justify-center mt-6">
              <Button variant="primary" size="lg" onClick={handleClose}>{npsText.closeButton}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default FeedbackModal
